import { useEffect } from "react";
import fetchPlusModalData from "~/common/PlusSubscriptionModal/api/fetchPlusModalData";
import {
  EnrollmentOption,
  PlusModalData,
} from "~/common/PlusSubscriptionModal/Models";
import { Throwable } from "@wss/error-tracking/throwable";
import { createGlobalStore } from "@wss/store";

type Complete<T> = { status: "complete"; response: Throwable<T> };
type Loading = { status: "loading" };
type Idle = { status: "idle" };
type ApiStatus<T> = Complete<T> | Loading | Idle;

export const plusSignupStore = createGlobalStore<ApiStatus<PlusModalData>>(
  () => ({
    status: "idle",
  }),
);

const request = async (): Promise<void> => {
  plusSignupStore.setState({ status: "loading" });

  const response = await fetchPlusModalData();

  plusSignupStore.setState({ status: "complete", response });
};

/**
 * Custom React hook for fetching and managing Plus Subscription modal data.
 *
 * This hook retrieves Plus modal data from the API, extracts available promotions,
 * and determines the default shipping address ID for the user. It also logs any errors
 * encountered during the fetch operation.
 *
 * @returns {{
 *   plusPromotions: EnrollmentOption[] | undefined,
 *   plusSignupState: PlusModalData | undefined,
 *   defaultShippingAddressId: number
 *   error: Error | undefined
 * }} An object containing the available Plus promotions, the raw Plus modal data,
 *     and the default shipping address ID.
 *
 * @example
 * const { plusPromotions, plusSignupState, defaultShippingAddressId, error } = usePlusModalData();
 */
export default function usePlusSignupState(): {
  plusPromotions: readonly EnrollmentOption[] | undefined;
  plusSignupState: PlusModalData | undefined;
  defaultShippingAddressId: number;
  error: Error | undefined;
} {
  const plusSignupState = plusSignupStore.useState();
  let error: Error | undefined;
  let plusPromotions = undefined;
  let defaultShippingAddressId = 0;
  if (plusSignupState.status === "complete") {
    if (!plusSignupState.response.isError) {
      const enrollmentOptions =
        plusSignupState.response.value?.enrollmentOptions ?? [];
      const validAddresses =
        plusSignupState.response.value?.validAddresses ?? [];
      plusPromotions = enrollmentOptions.filter(
        (option) => option.promotionName,
      );
      defaultShippingAddressId =
        validAddresses.find((address) => address.isDefault)?.id ??
        validAddresses[0]?.id ??
        0;
    } else {
      error = plusSignupState.response.error;
    }
  }

  useEffect(() => {
    const state = plusSignupStore.getState();
    if (state.status === "idle") {
      request();
    }
  }, []);

  return {
    plusPromotions,
    plusSignupState:
      plusSignupState.status === "complete" && !plusSignupState.response.isError
        ? plusSignupState.response.value
        : undefined,
    defaultShippingAddressId,
    error,
  };
}
